/**
 * Reference grid search with three filtering selects.
 *
 * Markup contract:
 * .reference-grid-search[data-endpoint][data-stage-language][data-type][data-limit]
 *   form.reference-grid-search__filters
 *     select[name="industry"]
 *     select[name="location"]
 *     select[name="offering"]
 *   div.reference-grid-search__status[aria-live="polite"]
 *   div.reference-grid-search__results
 *
 * Select names are sent to the endpoint as query parameters.
 *
 * @module reference-grid-search
 */

import { pick, escapeHtml, formatMessage, getStageLanguage, getImageUrl, resolveItemUrl, resolveReadMoreText } from './grid-utils.js';

const DEFAULT_ENDPOINT = '/stage-ajax/grid-search';
const DEFAULT_TYPE = 'references';
const DEFAULT_LIMIT = '12';
const DEFAULT_FETCH_LIMIT = '200';

/**
 * @typedef {Object} ReferenceGridTranslations
 * @property {string} loading
 * @property {string} loaded
 * @property {string} empty
 * @property {string} error
 * @property {string} loadMore
 * @property {string} readMore
 * @property {string} featured
 */

/**
 * @typedef {Object} NormalizedReferenceItem
 * @property {string} title
 * @property {string} description
 * @property {string} url
 * @property {(Object|string)} imageUrl - Either { jpg, webp } object or string URL.
 * @property {string} badge
 * @property {string} readMoreText
 * @property {string[]} industry
 * @property {string[]} location
 * @property {string} countryName
 * @property {string} companyName
 * @property {boolean} featured
 */

/** @type {ReferenceGridTranslations} */
const DEFAULT_TRANSLATIONS = {
  loading: 'Loading results...',
  loaded: '{shown} of {total} results loaded.',
  empty: 'No results found.',
  error: 'Unable to load results right now.',
  loadMore: 'Load more',
  readMore: 'Read more',
  featured: 'Featured'
};

/**
 * Reads localized UI strings from the component JSON script tag.
 *
 * @param {HTMLElement} root - Component root element.
 * @returns {ReferenceGridTranslations}
 */
function getTranslations(root) {
  const script = root.querySelector('.reference-grid-search__i18n');

  console.debug('[reference-grid] i18n script element:', script);
  console.debug('[reference-grid] i18n raw textContent:', script?.textContent);

  if (!(script instanceof HTMLScriptElement)) {
    console.debug('[reference-grid] i18n not found, using DEFAULT_TRANSLATIONS');
    return DEFAULT_TRANSLATIONS;
  }

  try {
    const parsed = JSON.parse(script.textContent || '{}');
    console.debug('[reference-grid] i18n parsed:', parsed);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return DEFAULT_TRANSLATIONS;
    }

    const result = { ...DEFAULT_TRANSLATIONS, ...parsed };
    console.debug('[reference-grid] i18n final translations:', result);
    return result;
  } catch (error) {
    console.debug('[reference-grid] i18n parse error:', error);
    return DEFAULT_TRANSLATIONS;
  }
}

/**
 * Normalizes one filter token for reliable comparisons.
 *
 * @param {string} value - Raw token value.
 * @returns {string}
 */
function normalizeFilterToken(value) {
  if (value === null || value === undefined) return '';

  const asString = String(value).trim();
  if (!asString) return '';

  try {
    return decodeURIComponent(asString).trim().toLowerCase();
  } catch {
    return asString.toLowerCase();
  }
}

/**
 * Converts API/form filter values to a normalized string array.
 *
 * @param {unknown} rawValue - Raw filter value from API or form.
 * @returns {string[]}
 */
function parseFilterValues(rawValue) {
  if (rawValue === null || rawValue === undefined) return [];

  if (Array.isArray(rawValue)) {
    return rawValue
      .map((entry) => normalizeFilterToken(entry))
      .filter(Boolean);
  }

  return String(rawValue)
    .split(',')
    .map((entry) => normalizeFilterToken(entry))
    .filter(Boolean);
}

/**
 * Resolves comma-separated filter ids to option labels from the active form.
 *
 * @param {HTMLFormElement|null} form - Filter form.
 * @param {string[]} selectNames - Candidate select names to inspect.
 * @param {string|null|undefined} rawValue - Raw comma-separated ids.
 * @returns {string[]}
 */
function resolveFilterLabels(form, selectNames, rawValue) {
  if (!form || !rawValue) return [];

  const ids = String(rawValue)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  if (!ids.length) return [];

  const labelMap = new Map();

  selectNames.forEach((selectName) => {
    const select = form.querySelector(`select[name="${selectName}"]`);
    if (!(select instanceof HTMLSelectElement)) return;

    Array.from(select.options).forEach((option) => {
      if (option.value) {
        labelMap.set(option.value, option.textContent?.trim() || option.value);
      }
    });
  });

  return ids
    .map((id) => labelMap.get(id))
    .filter(Boolean);
}

/**
 * Normalizes one API item into the card shape used by the UI.
 *
 * @param {Record<string, any>} item - Raw API item.
 * @param {HTMLFormElement|null} form - Filter form for label resolution.
 * @param {ReferenceGridTranslations} translations - Localized UI strings.
 * @returns {NormalizedReferenceItem}
 */
function normalizeItem(item, form = null, translations = DEFAULT_TRANSLATIONS) {
  const badge = pick(item, ['badge', 'label', 'type', 'category']) || '';
  const featuredValue = pick(item, ['featured', 'isFeatured', 'is_featured']);

  return {
    title: pick(item, ['title', 'headline', 'heading', 'name']) || '',
    description: pick(item, ['description', 'excerpt', 'summary', 'ingress']) || '',
    url: resolveItemUrl(item),
    imageUrl: getImageUrl(item),
    badge,
    readMoreText: resolveReadMoreText(item, translations),
    industry: resolveFilterLabels(
      form,
      ['industry', 'filter_industry'],
      pick(item, ['filter_industry', 'industry', 'industryName', 'industry_name'])
    ),
    location: resolveFilterLabels(
      form,
      ['location', 'country', 'filter_country'],
      pick(item, ['filter_country', 'location', 'locationName', 'location_name'])
    ),
    countryName: String(pick(item, ['country_name', 'countryName']) || '').trim(),
    companyName: String(pick(item, ['company_name', 'companyName']) || '').trim(),
    featured: featuredValue === 1 || featuredValue === '1',
    // Raw filter fields preserved for client-side filter matching.
    _filter_industry: pick(item, ['filter_industry', 'industry', 'industryName', 'industry_name']),
    _filter_country: pick(item, ['filter_country', 'country', 'location', 'locationName', 'location_name']),
    _filter_offering: pick(item, ['filter_offering', 'offering'])
  };
}

/**
 * Extracts an item array from a JSON response.
 *
 * @param {Record<string, any>} payload - Parsed response payload.
 * @returns {Record<string, any>[]}
 */
function extractItems(payload) {
  const directItems = pick(payload, ['items', 'results', 'references', 'data']);
  if (Array.isArray(directItems)) return directItems;

  if (payload?.data && typeof payload.data === 'object') {
    const nestedItems = pick(payload.data, ['items', 'results', 'references']);
    if (Array.isArray(nestedItems)) return nestedItems;
  }

  return [];
}

/**
 * Extracts the total result count from a JSON response.
 *
 * @param {Record<string, any>} payload - Parsed response payload.
 * @returns {number}
 */
function extractTotal(payload) {
  const total = pick(payload, ['total', 'count']);
  return Number.isFinite(Number(total)) ? Number(total) : extractItems(payload).length;
}

/**
 * Builds the request URL from component data and form values.
 *
 * @param {HTMLElement} root - Component root element.
 * @param {number} limit - Fetch limit for the initial request.
 * @returns {URL}
 */
function buildRequestUrl(root, limit) {
  const endpoint = root.dataset.endpoint || DEFAULT_ENDPOINT;
  const requestUrl = new URL(endpoint, window.location.origin);

  requestUrl.searchParams.set('stage-language', getStageLanguage(root));
  requestUrl.searchParams.set('type', root.dataset.type || DEFAULT_TYPE);
  requestUrl.searchParams.set('limit', String(limit));

  const gridSearch = root.dataset.gridSearch?.trim();
  if (gridSearch) {
    requestUrl.searchParams.set('grid_search', gridSearch);
  }

  return requestUrl;
}

/**
 * Creates responsive picture markup with webp support.
 *
 * @param {Object} imageData - Image URLs object.
 * @param {string} imageData.jpg - JPEG image URL.
 * @param {(string|null)} imageData.webp - WebP image URL, or null if unavailable.
 * @returns {string}
 */
function renderPictureMarkup(imageData) {
  if (!imageData?.jpg) {
    return '';
  }

  const webpSource = imageData.webp
    ? `<source srcset="${escapeHtml(imageData.webp)}" type="image/webp">`
    : '';

  return `<picture>
    ${webpSource}
    <img src="${escapeHtml(imageData.jpg)}" alt="">
  </picture>`;
}

/**
 * Creates one card HTML string.
 *
 * @param {NormalizedReferenceItem} item - Normalized card item.
 * @returns {string}
 */
function renderCard(item) {
  const themeClass = item.featured ? ' reference-grid-search__card--featured' : '';

  let imageMarkup;
  if (item.imageUrl) {
    // If imageUrl is an object with jpg/webp, use picture element.
    if (typeof item.imageUrl === 'object' && item.imageUrl.jpg) {
      imageMarkup = `<div class="reference-grid-search__card-media">${renderPictureMarkup(item.imageUrl)}</div>`;
    } else if (typeof item.imageUrl === 'string') {
      // Fallback: plain string image URL.
      imageMarkup = `<div class="reference-grid-search__card-media"><img src="${escapeHtml(item.imageUrl)}" alt=""></div>`;
    } else {
      imageMarkup = '<div class="reference-grid-search__card-media reference-grid-search__card-media--empty" aria-hidden="true"></div>';
    }
  } else {
    imageMarkup = '<div class="reference-grid-search__card-media reference-grid-search__card-media--empty" aria-hidden="true"></div>';
  }

  // Keep card meta focused on company and country only.
  const locationLine = [item.companyName, item.countryName].filter(Boolean).join(', ');

  return `
    <article class="reference-grid-search__card${themeClass}">
      <a class="reference-grid-search__card-link" href="${escapeHtml(item.url)}">
        <div class="reference-grid-search__card-shell">
          ${imageMarkup}
          ${item.badge ? `<span class="reference-grid-search__badge">${escapeHtml(item.badge)}</span>` : ''}
          <div class="reference-grid-search__card-content">
            <h3 class="reference-grid-search__card-title">${escapeHtml(item.title)}</h3>
            ${locationLine ? `<p class="reference-grid-search__meta reference-grid-search__meta--large">${locationLine}</p>` : ''}
            ${item.description ? `<p class="reference-grid-search__card-text">${escapeHtml(item.description)}</p>` : ''}
            <span class="btn btn-white reference-grid-search__card-cta" aria-hidden="true">
              ${escapeHtml(item.readMoreText)}
              <svg viewBox="0 0 16 16" focusable="false" height="12" width="12" aria-hidden="true">
                <use href="#chevron-right"></use>
              </svg>
            </span>
          </div>
        </div>
      </a>
    </article>
  `;
}

/**
 * Updates the status text.
 *
 * @param {HTMLElement|null} status - Live region element.
 * @param {string} message - Status message.
 * @returns {void}
 */
function updateStatus(status, message) {
  if (status) {
    status.textContent = message;
  }
}

/**
 * Checks whether an item matches the current filter values.
 *
 * @param {Record<string, any>} item - Raw API item.
 * @param {HTMLFormElement} form - Filter form.
 * @returns {boolean}
 */
function matchesFilters(item, form) {
  const formData = new FormData(form);
  const activeFilters = Array.from(formData.entries()).filter(([, value]) => typeof value === 'string' && value.trim());

  if (!activeFilters.length) return true;

  const filterKeyMap = {
    industry: ['_filter_industry', 'filter_industry', 'industry'],
    location: ['_filter_country', 'filter_country', 'country', 'location'],
    offering: ['_filter_offering', 'filter_offering', 'offering', '_filter_industry', 'filter_industry', 'industry']
  };

  return activeFilters.every(([key, value]) => {
    const candidateKeys = filterKeyMap[key] || [key, `filter_${key}`];
    const candidateValues = candidateKeys
      .map((candidateKey) => item?.[candidateKey])
      .filter((candidateValue) => candidateValue !== undefined && candidateValue !== null);

    if (!candidateValues.length) return false;

    const values = Array.from(new Set(candidateValues.flatMap((candidateValue) => parseFilterValues(candidateValue))));
    const selectedValue = normalizeFilterToken(value);
    const isMatch = values.includes(selectedValue);

    return isMatch;
  });
}

/**
 * Initialises one reference grid search root.
 *
 * @param {HTMLElement} root - Component root element.
 * @returns {void}
 */
function initReferenceGrid(root) {
  if (root.dataset.referenceGridInitialized === 'true') return;

  const form = root.querySelector('.reference-grid-search__filters');
  const results = root.querySelector('.reference-grid-search__results');
  const status = root.querySelector('.reference-grid-search__status');
  const loadMoreButton = root.querySelector('.reference-grid-search__load-more');
  const translations = getTranslations(root);

  if (!(form instanceof HTMLFormElement) || !(results instanceof HTMLElement)) return;

  root.dataset.referenceGridInitialized = 'true';

  /** @type {AbortController | null} */
  let activeRequest = null;
  const baseLimit = Number.parseInt(root.dataset.limit || DEFAULT_LIMIT, 10) || Number.parseInt(DEFAULT_LIMIT, 10);
  const fetchLimit = Number.parseInt(root.dataset.fetchLimit || DEFAULT_FETCH_LIMIT, 10) || Number.parseInt(DEFAULT_FETCH_LIMIT, 10);
  /** @type {Record<string, any>[]} */
  let allItems = [];
  let currentLimit = baseLimit;
  let currentCount = 0;
  let totalCount = 0;

  /**
   * Updates the load-more button visibility and state.
   *
   * @returns {void}
   */
  function updateLoadMoreState() {
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    const hasMore = currentCount < totalCount;
    loadMoreButton.hidden = !hasMore;
    loadMoreButton.disabled = root.dataset.loading === 'true';
    loadMoreButton.textContent = translations.loadMore;
  }

  /**
   * Renders the currently visible slice from the cached result set.
   *
   * @param {number|null} focusIndex - Index of the card link to focus after render.
   * @returns {void}
   */
  function renderCurrentResults(focusIndex = null) {
    const filteredItems = allItems
      .filter((item) => matchesFilters(item, form))
      .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    const visibleItems = filteredItems.slice(0, currentLimit);

    results.innerHTML = visibleItems.length
      ? visibleItems.map(renderCard).join('')
      : `<p class="reference-grid-search__empty">${escapeHtml(translations.empty)}</p>`;

    currentCount = visibleItems.length;
    totalCount = filteredItems.length;
    updateStatus(status, formatMessage(translations.loaded, {
      shown: currentCount,
      total: totalCount
    }));
    updateLoadMoreState();

    if (Number.isInteger(focusIndex) && focusIndex >= 0) {
      const cardLinks = results.querySelectorAll('.reference-grid-search__card-link');
      const targetLink = cardLinks[focusIndex];

      if (targetLink instanceof HTMLElement) {
        requestAnimationFrame(() => {
          targetLink.focus();
        });
      }
    }
  }

  /**
   * Fetches the full reference set once.
   *
   * @returns {Promise<void>}
   */
  async function fetchAllResults() {
    activeRequest?.abort();
    activeRequest = new AbortController();

    const requestUrl = buildRequestUrl(root, fetchLimit);
    root.dataset.loading = 'true';
    updateStatus(status, translations.loading);
    updateLoadMoreState();

    try {
      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json, text/html'
        },
        signal: activeRequest.signal
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const payload = await response.json();
      allItems = extractItems(payload).map((item) => normalizeItem(item, form, translations));

      totalCount = extractTotal(payload);
      renderCurrentResults();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      results.innerHTML = `<p class="reference-grid-search__empty">${escapeHtml(translations.error)}</p>`;
      updateStatus(status, translations.error);
      allItems = [];
      currentCount = 0;
      totalCount = 0;
    } finally {
      root.dataset.loading = 'false';
      updateLoadMoreState();
    }
  }

  form.addEventListener('change', (event) => {
    if (event.target instanceof HTMLSelectElement) {
      currentLimit = baseLimit;
      currentCount = 0;
      totalCount = 0;
      renderCurrentResults();
    }
  });

  if (loadMoreButton instanceof HTMLButtonElement) {
    loadMoreButton.addEventListener('click', () => {
      const firstNewItemIndex = currentCount;
      currentLimit += baseLimit;
      renderCurrentResults(firstNewItemIndex);
    });
  }

  fetchAllResults();
}

/**
 * Initialises all reference grid search sections.
 *
 * @returns {void}
 */
export function initReferenceGridSearch() {
  const sections = document.querySelectorAll('.reference-grid-search');
  sections.forEach((section) => {
    if (section instanceof HTMLElement) {
      initReferenceGrid(section);
    }
  });
}
